import React, { useState } from 'react';

// Helper function to get initials from student name
const getStudentInitials = (student) => {
  const firstName = student.first_name || student.firstName || student.name?.split(' ')[0] || '';
  const lastName = student.last_name || student.lastName || student.name?.split(' ')[1] || '';
  return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
};

export default function AttendanceStudentRow({ student, status, setStatus, note, setNote }) {
  const [showNoteField, setShowNoteField] = useState(false);

  const handleNoteToggle = () => {
    setShowNoteField(!showNoteField);
  };

  const handleNoteChange = (e) => {
    if (setNote) {
      const value = e.target.value;
      // Enforce 100 character limit
      if (value.length <= 100) {
        setNote(value);
      }
    }
  };

  return (
    <div className={`
      ${student.isHistoricalRecord ? 'bg-gradient-to-r from-amber-25 to-amber-50 border-l-4 border-l-amber-300' : ''}
    `}>
      {/* Main student row */}
      <div className="flex items-center justify-between py-3 px-3">
        <div className="flex items-center gap-3">
          {/* Note icon */}
          <button
            onClick={handleNoteToggle}
            className={`p-2 rounded-full transition-colors ${
              showNoteField || note 
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title={showNoteField ? "Hide note" : "Add note"}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3-8.25h3.375c.621 0 1.125.504 1.125 1.125V19.5a2.25 2.25 0 01-2.25 2.25h-10.5A2.25 2.25 0 013 19.5V4.875c0-.621.504-1.125 1.125-1.125H5.25c.621 0 1.125.504 1.125 1.125v.375m0 0V8.25c0 .621.504 1.125 1.125 1.125h8.25c.621 0 1.125-.504 1.125-1.125V6.75c0-.621-.504-1.125-1.125-1.125H7.5z" />
            </svg>
          </button>
          
          <div className="relative hidden sm:block">
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center ${student.isHistoricalRecord ? 'opacity-75' : ''}`}>
              <span className="text-sm font-medium text-white">
                {getStudentInitials(student)}
              </span>
            </div>
            {student.isHistoricalRecord && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
          
          <div className="flex flex-col">
            <span className={`font-medium text-base ${student.isHistoricalRecord ? 'text-slate-600' : 'text-slate-700'}`}>
              {student.name || `Student ${student.id}`}
            </span>
            {student.isHistoricalRecord && (
              <span className="text-xs text-amber-700 font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                No longer enrolled
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {/* Clear status button */}
          <button
            onClick={() => setStatus(undefined)}
            className={`px-3 py-2 rounded-lg text-sm font-semibold border transition
              ${status === undefined
                ? 'bg-gray-100 text-gray-700 border-gray-300'
                : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-gray-100 hover:text-gray-600'
              }
            `}
            title="Clear status"
          >
            ✕
          </button>
          
          {/* Status buttons */}
          {['present', 'absent', 'late', 'excused'].map(type => (
            <button
              key={type}
              onClick={() => setStatus(type)}
              className={`px-3 sm:px-8 py-2 rounded-lg text-sm font-semibold border transition
                ${status === type
                  ? type === 'present'
                    ? 'bg-green-100 text-green-700 border-green-300'
                    : type === 'absent'
                    ? 'bg-red-100 text-red-700 border-red-300'
                    : type === 'late'
                    ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                    : type === 'excused'
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-slate-50 text-slate-400 border-slate-200'
                  : 'bg-slate-50 text-slate-400 border-slate-200'
                }
                ${typeof status === 'undefined' ? 'bg-slate-50 text-slate-400 border-slate-200' : ''}
              `}
            >
              {/* Show abbreviated text on small screens, full text on larger screens */}
              <span className="sm:hidden">
                {type.charAt(0).toUpperCase()}
              </span>
              <span className="hidden sm:inline">
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Note field (appears when note icon is clicked) */}
      {showNoteField && (
        <div className="px-3 pb-3">
          <textarea
            value={note || ''}
            onChange={handleNoteChange}
            placeholder="Add a note for this student..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            rows="1"
            maxLength="100"
            autoFocus
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {(note || '').length}/100 characters
          </div>
        </div>
      )}
    </div>
  );
} 